import { ControlledDateField, ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { ConstructionTypeData, BimActKc2VersionOtherItemData } from 'gql/graphql';
import { bimActKc2OtherStatusesListItems } from 'pages/Project/BimProject/WorkOrder/types';
import { ConstructionTypeIcon } from 'pages/Project/Common/types';
import type { IListItem } from 'types/Contract';
import type { IconComponent } from 'uikit';
import { Grid, GridItem } from 'uikit';
import { newDate } from 'utils';

import { useCallback, useEffect, useMemo } from 'react';
import type React from 'react';
import { useForm } from 'react-hook-form';

import { useBimVersionActKC2Other } from './hooks/useBimVersionActKC2Other';

interface IBimActKc2VersionOtherRead {
    actKC2Other?: BimActKc2VersionOtherItemData;
    onClose: (isSuccess: boolean) => void;
    workOrderId?: number;
    constructionType?: ConstructionTypeData;
    selectedObject?: IListItem<number>;
    priceTableApiRef?: React.RefObject<any>;
}

export const BimActKc2VersionOtherRead: React.FC<IBimActKc2VersionOtherRead> = (props) => {
    const { actKC2Other, onClose, constructionType, selectedObject } = props;

    const actKC2VersionOtherId = actKC2Other?.id;

    const { control, setValue } = useForm({
        mode: 'onChange',
        defaultValues: {
            constructionType: constructionType?.name,
            objectName: selectedObject?.label,
        } as any,
    });

    const { bimActKC2OtherData, bimActKC2OtherStatus } = useBimVersionActKC2Other(actKC2VersionOtherId);

    const isLoading = useMemo(() => bimActKC2OtherStatus === RequestStatus.FETCHING, [bimActKC2OtherStatus]);

    const statusItems = useMemo(() => [bimActKc2OtherStatusesListItems[0]], []);

    const seasonsItems = useMemo(
        () => [
            {
                id: bimActKC2OtherData?.bimActKC2VersionOther?.data?.workOrderVersionSeason?.id,
                label: bimActKC2OtherData?.bimActKC2VersionOther?.data?.workOrderVersionSeason?.year,
            },
        ],
        [
            bimActKC2OtherData?.bimActKC2VersionOther?.data?.workOrderVersionSeason?.id,
            bimActKC2OtherData?.bimActKC2VersionOther?.data?.workOrderVersionSeason?.year,
        ],
    );

    const onCloseLocal = useCallback(() => {
        onClose(false);
    }, [onClose]);

    const onModalClose = useCallback(() => {
        onCloseLocal();
    }, [onCloseLocal]);

    useEffect(() => {
        if (bimActKC2OtherData?.bimActKC2VersionOther?.data) {
            setValue('season', {
                id: bimActKC2OtherData?.bimActKC2VersionOther?.data?.workOrderVersionSeason?.id,
                label: bimActKC2OtherData?.bimActKC2VersionOther?.data?.workOrderVersionSeason?.year,
            });
            setValue(
                'status',
                bimActKc2OtherStatusesListItems.find(
                    (s) => s.id === bimActKC2OtherData?.bimActKC2VersionOther?.data?.status,
                ),
            );

            setValue('number', bimActKC2OtherData?.bimActKC2VersionOther?.data?.number);

            setValue('date', newDate(bimActKC2OtherData?.bimActKC2VersionOther?.data?.date));
            setValue('reportingDate', [
                newDate(bimActKC2OtherData?.bimActKC2VersionOther?.data?.startDate),
                newDate(bimActKC2OtherData?.bimActKC2VersionOther?.data?.finishDate),
            ]);
            setValue('contractor', bimActKC2OtherData?.bimActKC2VersionOther?.data?.contractor);
            setValue('contractorAddress', bimActKC2OtherData?.bimActKC2VersionOther?.data?.contractorAddress);
        }
    }, [bimActKC2OtherData?.bimActKC2VersionOther?.data, seasonsItems, setValue]);

    return (
        <ModalDialog
            title="Реквизиты Акта КС-2"
            isOpen
            isOnClickOutsideDisable
            btnConfirmLabel="Сохранить"
            style={{ width: 600 }}
            onClose={onModalClose}
            isLoading={isLoading}
            btnConfirmDisabled
            bodyWidth={600}
        >
            <div style={{ padding: 20 }}>
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledSelectField
                            name="status"
                            label="Статус"
                            control={control}
                            items={statusItems}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="season"
                            label="Сезон"
                            control={control}
                            items={seasonsItems}
                            disabled
                            required
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="group"
                            label="Группа затрат"
                            control={control}
                            type="text"
                            view="clear"
                            value="Прочие"
                            readOnly
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="objectName"
                            label="Объект"
                            control={control}
                            type="text"
                            view="clear"
                            readOnly
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="constructionType"
                            label="Вид строительства"
                            control={control}
                            type="text"
                            view="clear"
                            readOnly
                            leftSide={
                                constructionType && (ConstructionTypeIcon[constructionType.code] as IconComponent)
                            }
                            iconSize="m"
                        />
                    </GridItem>
                    <GridItem>
                        <div className="ActKC2FieldWrapper">
                            <ControlNumberField
                                name="number"
                                label="Номер"
                                labelPosition="left"
                                control={control}
                                size="s"
                                decimalScale={0}
                                direction="upCenter"
                                disabled
                            />
                            <ControlledDateField name="date" label="от" control={control} required disabled />
                        </div>
                    </GridItem>
                    <GridItem>
                        <ControlledDateField
                            name="reportingDate"
                            type="date-range"
                            label="Отчетный период"
                            control={control}
                            required
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="contractor"
                            label="Подрядчик"
                            control={control}
                            type="text"
                            placeholder="Подрядчик"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="contractorAddress"
                            label="Адрес Подрядчика"
                            control={control}
                            type="text"
                            placeholder="не указан"
                            disabled
                        />
                    </GridItem>
                </Grid>
            </div>
        </ModalDialog>
    );
};

BimActKc2VersionOtherRead.displayName = 'BimActKc2VersionOtherRead';
